import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import SectionHeading from "@/components/base/SectionHeading";
import MovieCard from "@/components/feature/MovieCard";
import HeroCarousel from "./components/HeroCarousel";
import QuickEntries from "./components/QuickEntries";
import PopularPanel from "./components/PopularPanel";
import { movies } from "@/mocks/movies";
import { newsList } from "@/mocks/news";
import { reviews } from "@/mocks/reviews";

export default function Home() {
  useDocumentMeta(
    "小黄人电影－最新影视资讯与剧情介绍",
    "小黄人电影提供最新的影视资讯、剧情介绍和影评，覆盖小黄人系列电影及相关动画作品，满足影迷全方位了解需求。"
  );
  const latestMovies = [...movies].sort((a, b) => b.year - a.year).slice(0, 6);
  const hotNews = [...newsList].sort((a, b) => b.views - a.views).slice(0, 5);
  const hotReviews = [...reviews].sort((a, b) => b.views - a.views).slice(0, 5);

  return (
    <SiteLayout>
      <HeroCarousel />
      <QuickEntries />

      <section className="bg-background-100 py-14 md:py-16">
        <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6">
          <SectionHeading
            eyebrow="最新发布"
            icon="ri-movie-2-line"
            title="最新电影"
            desc="按上映年份为你整理的最新作品，看看最近又有哪些小黄人相关的欢乐上映了。"
            action={
              <Link
                to="/category"
                className="flex items-center gap-1.5 rounded-full border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 transition hover:border-primary-400 hover:text-primary-700"
              >
                查看全部影片
                <i className="ri-arrow-right-s-line"></i>
              </Link>
            }
          />
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-3 lg:gap-6">
            {latestMovies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-14 md:px-6 md:py-16">
        <SectionHeading
          eyebrow="编辑精选"
          icon="ri-fire-line"
          title="热门资讯与影评"
          desc="点击率最高的小黄人新闻与最受欢迎的影评，一次补齐本周的热门话题。"
        />
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <PopularPanel
            title="热门资讯"
            icon="ri-newspaper-line"
            moreTo="/news"
            items={hotNews.map((n) => ({
              to: `/news/${n.id}`,
              title: n.title,
              meta: `${n.date} · ${n.category}`,
            }))}
          />
          <PopularPanel
            title="热门影评"
            icon="ri-chat-quote-line"
            moreTo="/reviews"
            items={hotReviews.map((r) => ({
              to: `/reviews/${r.id}`,
              title: r.title,
              meta: `${r.author} · 评分 ${r.rating}`,
            }))}
          />
        </div>
      </section>

      <section className="bg-background-100 py-14 md:py-16">
        <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6">
          <div className="overflow-hidden rounded-3xl bg-primary-500">
            <div className="grid grid-cols-1 items-center gap-8 p-8 md:grid-cols-2 md:p-12">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-foreground-950/10 px-3.5 py-1.5 text-xs font-semibold text-foreground-950">
                  <i className="ri-emotion-happy-line"></i>
                  关于小黄人电影
                </span>
                <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
                  把最纯粹的快乐，讲给每一个喜欢小黄人的你
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-foreground-900/85 md:text-base">
                  小黄人电影是一个专注小黄人系列及相关动画作品的内容站。我们整理完整的影片资料、撰写通俗易懂的剧情介绍，也收录来自影迷的真实影评，希望帮助你在一处就能了解所有想了解的内容。
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/about"
                    className="flex items-center gap-2 rounded-full bg-foreground-950 px-6 py-3 text-sm font-semibold text-background-50 transition hover:bg-foreground-900"
                  >
                    <i className="ri-information-line text-lg"></i>
                    了解我们
                  </Link>
                  <Link
                    to="/category"
                    className="flex items-center gap-2 rounded-full border border-foreground-950/25 px-6 py-3 text-sm font-semibold text-foreground-950 transition hover:bg-foreground-950/5"
                  >
                    <i className="ri-movie-2-line text-lg"></i>
                    开始浏览
                  </Link>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { num: `${movies.length}+`, label: "收录影片" },
                  { num: `${newsList.length}+`, label: "影视资讯" },
                  { num: `${reviews.length}+`, label: "影评文章" },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="rounded-2xl bg-background-50/80 p-4 text-center"
                  >
                    <p className="font-heading text-2xl font-bold text-foreground-950">
                      {stat.num}
                    </p>
                    <p className="mt-1 text-xs text-foreground-600">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}